import React from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { authApi, TenantBranding } from '../api/auth';
import PlatformLanding from '../platform/pages/Landing';
import TenantSite from './TenantSite';
import MinimalLanding from './MinimalLanding';

/**
 * Dispatcher for "/". Ported from grant_management's server-driven approach: the
 * platform-vs-tenant decision comes from `getBranding` (a `name` iff a tenant is
 * in context), never from parsing the host. The full-site decision is likewise
 * server-driven — `branding.full_site_enabled`.
 *
 * A tenant subdomain has a *public* site, so an anonymous visitor there sees the
 * CMS site (full_site on) or a minimal landing (off) — org admins always get the
 * CMS site, so they can keep building it out before flipping it public. The
 * platform domain (no branding name) shows the SGOKit marketing home.
 */
const RootRedirect: React.FC = () => {
  const { user, loading, isOrgAdmin, isSuperuser } = useAuth();

  const { data: branding, isLoading: brandingLoading } = useQuery<TenantBranding>({
    queryKey: ['tenant_branding'],
    queryFn: authApi.getBranding,
    staleTime: 10 * 60 * 1000,
  });

  if (loading || brandingLoading) {
    return <div data-testid="root-loading">Loading…</div>;
  }

  // A signed-in system admin on the platform domain goes to the platform
  // console. On a tenant's subdomain they're an org admin like any other (see
  // UserSerializer.get_capabilities) and fall through to the tenant's site.
  if (user && isSuperuser && !branding?.name) {
    return <Navigate to="/superadmin" replace />;
  }

  // On the platform domain (no tenant in context) a signed-in org admin lands
  // on the console — there's no tenant site to edit there. On their own
  // tenant subdomain they instead see their public site, like every other
  // page path (see App.tsx's "*" route), with the CMS toolbar always present.
  if (user && isOrgAdmin && !branding?.name) {
    return <Navigate to="/settings/donations" replace />;
  }

  // No tenant name ⇒ the platform domain ⇒ the SGOKit platform root, currently
  // just the logo (the marketing home is on hold — see platform/pages/Landing.tsx).
  if (!branding?.name) {
    return <PlatformLanding />;
  }

  // A tenant subdomain: the CMS site when the full site is enabled, else a
  // minimal landing. /donate and /apply resolve directly either way.
  if (!branding.full_site_enabled && !isOrgAdmin) {
    return <MinimalLanding />;
  }

  return <TenantSite />;
};

export default RootRedirect;
